
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useCustomerData } from '@/hooks/useCustomerData';
import { TimeRange, HistoricalStat, DailyNewSubscription } from './stats/types';
import { generateHistoricalData } from './stats/historicalDataGenerator';
import { calculateNewSubscriptionsByDay } from './stats/newSubscriptionsGenerator';

export const useAdminStats = () => {
  const [historicalStats, setHistoricalStats] = useState<HistoricalStat[]>([]);
  const [newSubscriptionsByDay, setNewSubscriptionsByDay] = useState<DailyNewSubscription[]>([]);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState<boolean>(true);
  const [errorHistorical, setErrorHistorical] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const { customers, isLoading: isLoadingCustomers } = useCustomerData();
  const { toast } = useToast();
  
  const fetchHistoricalStats = useCallback(async () => {
    setIsLoadingHistorical(true);
    setErrorHistorical(null);
    
    try {
      // In a real implementation, we would fetch this data from a dedicated endpoint
      // that has processed historical data stored in a table
      // For now, we'll generate it from the current customers data
      
      if (isLoadingCustomers) {
        // Wait for customer data to load
        return;
      }
      
      console.log("Generating historical data from", customers.length, "customers");
      const sampleCustomers = customers.slice(0, 5);
      console.log("Sample customer data:", sampleCustomers.map(c => ({ 
        id: c.id.substring(0, 8), 
        plan: c.plan, 
        status: c.status,
        created_at: c.created_at 
      })));
      
      const historicalData = generateHistoricalData(timeRange, customers);
      setHistoricalStats(historicalData);
      
      // Calculate new subscriptions by day for the last 7 days
      const newSubscriptionsData = calculateNewSubscriptionsByDay(customers);
      setNewSubscriptionsByDay(newSubscriptionsData);
    } catch (err: any) {
      console.error('Error generating historical stats:', err);
      setErrorHistorical(err.message || 'Failed to load historical data');
      toast({
        title: 'Error loading stats',
        description: err.message || 'An error occurred while loading historical data',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistorical(false);
    }
  }, [timeRange, toast, customers, isLoadingCustomers]);
  
  useEffect(() => {
    fetchHistoricalStats();
  }, [fetchHistoricalStats]);
  
  return {
    historicalStats,
    newSubscriptionsByDay,
    isLoadingHistorical,
    errorHistorical,
    timeRange,
    setTimeRange,
    refreshHistoricalStats: fetchHistoricalStats
  };
};
